/**
 * PlanRoomPanel — the Sets/Issue/Transmittals band inside the Plan Room page.
 *
 * Renders above the existing sheet grid on `DrawingControlPage`. Reads the
 * project's issued sets + transmittals; offers "Issue Set" (bundle upload
 * OR pick from already-uploaded drawings) and a transmittal drawer.
 *
 * Stage a/b/c/e only — portal serving (stage d) lives elsewhere.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChevronDown, ChevronRight, Send, PackageCheck, FilePlus2, X, Users,
  Search, Star, PlusCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import RulebookShortcut from './rulebook/RulebookShortcut';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanRoomPanel({ projectId, drawings, darkMode, onSetIssued }) {
  const [sets, setSets] = useState([]);
  const [transmittals, setTransmittals] = useState([]);
  const [phases, setPhases] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [showTransmit, setShowTransmit] = useState(null);   // set id
  const [showTxDetail, setShowTxDetail] = useState(null);   // transmittal object

  useEffect(() => {
    if (!projectId) return;
    load();
    axios.get(`${API}/plan-room/phases`).then(r => setPhases(r.data.phases || [])).catch(() => {});
  }, [projectId]);

  const load = async () => {
    try {
      const [setsRes, txRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/plan-room/sets`),
        axios.get(`${API}/projects/${projectId}/plan-room/transmittals`),
      ]);
      setSets(setsRes.data.sets || []);
      setTransmittals(txRes.data.transmittals || []);
    } catch (e) {
      // Silent: Plan Room panel is additive; drawings still work.
    }
  };

  const currentSets = sets.filter(s => s.status === 'current');
  const supersededSets = sets.filter(s => s.status === 'superseded');

  return (
    <>
      <section
        data-testid="plan-room-panel"
        className={`border rounded-lg mb-6 ${darkMode ? 'border-[#2A2A2A] bg-[#141414]' : 'border-[#E5E2DD] bg-white'}`}
      >
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={`w-full flex items-center justify-between p-4 ${darkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-gray-50'}`}
          data-testid="plan-room-toggle-btn"
        >
          <div className="flex items-center gap-3">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <PackageCheck className={`w-5 h-5 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`} />
            <div className="text-left">
              <h2 className={`font-heading font-light text-base flex items-center gap-2 ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>
                Issued Sets
                <RulebookShortcut nodeId="projects.inside.tools.plan-room" />
              </h2>
              <p className={`text-xs ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                {currentSets.length} current · {supersededSets.length} superseded · {transmittals.length} transmittals
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); setShowIssue(true); }}
            className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            data-testid="plan-room-issue-set-btn"
          >
            <FilePlus2 className="w-4 h-4 mr-2" /> Issue Set
          </Button>
        </button>

        {expanded && (
          <div className="p-4 pt-0">
            {sets.length === 0 ? (
              <div className={`text-center py-8 rounded ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`}>
                <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                  No sets issued yet. Upload drawings below, then click <b>Issue Set</b> to group and transmit them.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sets.map(s => (
                  <SetRow
                    key={s.id}
                    set={s}
                    darkMode={darkMode}
                    onTransmit={() => setShowTransmit(s.id)}
                    transmittalCount={transmittals.filter(t => t.set_id === s.id).length}
                  />
                ))}
              </div>
            )}

            {transmittals.length > 0 && (
              <div className="mt-6 pt-4 border-t border-dashed" style={{ borderColor: darkMode ? '#2A2A2A' : '#E5E2DD' }}>
                <h3 className={`text-xs uppercase tracking-wide font-semibold mb-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
                  Recent transmittals
                </h3>
                <div className="space-y-1" data-testid="plan-room-transmittal-list">
                  {transmittals.slice(0, 5).map(tx => (
                    <button
                      key={tx.id}
                      onClick={() => setShowTxDetail(tx)}
                      className={`w-full text-left flex items-center justify-between px-3 py-2 rounded text-xs ${darkMode ? 'hover:bg-[#1A1A1A] text-[#E5E2DD]' : 'hover:bg-gray-50 text-[#1A1918]'}`}
                      data-testid={`plan-room-tx-${tx.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Send className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          <b>{tx.set_name}</b> · {tx.recipients.length} recipient{tx.recipients.length === 1 ? '' : 's'} · {new Date(tx.sent_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{tx.method}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {showIssue && (
        <IssueSetDialog
          projectId={projectId}
          drawings={drawings}
          phases={phases}
          onClose={() => setShowIssue(false)}
          onIssued={async () => {
            setShowIssue(false);
            await load();
            onSetIssued && onSetIssued();
            toast.success('Set issued');
          }}
        />
      )}
      {showTransmit && (
        <TransmitDialog
          projectId={projectId}
          setId={showTransmit}
          onClose={() => setShowTransmit(null)}
          onSent={async () => {
            setShowTransmit(null);
            await load();
            toast.success('Transmittal sent');
          }}
        />
      )}
      {showTxDetail && (
        <TxDetailDialog transmittal={showTxDetail} projectId={projectId} onClose={() => setShowTxDetail(null)} />
      )}
    </>
  );
}

function SetRow({ set, darkMode, onTransmit, transmittalCount }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`rounded border ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-[#FAF9F7]'}`}
      data-testid={`plan-room-set-${set.id}`}
    >
      <button
        type="button"
        className={`w-full flex items-center justify-between p-3 ${darkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-white'}`}
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
          <div className="text-left min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-medium truncate ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>{set.name}</span>
              <Badge variant={set.status === 'current' ? 'default' : 'secondary'} className="text-[10px]">
                {set.status === 'current' ? 'Current' : 'Superseded'}
              </Badge>
              <Badge variant="outline" className="text-[10px]">{set.phase}</Badge>
              {set.is_legacy && <Badge variant="outline" className="text-[10px]">Legacy</Badge>}
            </div>
            <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}`}>
              {set.version_refs?.length || 0} sheet{set.version_refs?.length === 1 ? '' : 's'} ·
              Issued {set.issue_date} ·
              {transmittalCount} transmittal{transmittalCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        {set.status === 'current' && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onTransmit(); }}
            data-testid={`plan-room-transmit-${set.id}-btn`}
          >
            <Send className="w-3 h-3 mr-1" /> Transmit
          </Button>
        )}
      </button>
      {open && set.version_refs?.length > 0 && (
        <div className={`px-6 py-2 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-[#E5E2DD]'}`}>
          <table className="w-full text-xs">
            <thead>
              <tr className={darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]'}>
                <th className="text-left py-1">Sheet</th>
                <th className="text-left py-1">Title</th>
                <th className="text-left py-1">Rev</th>
              </tr>
            </thead>
            <tbody>
              {set.version_refs.map(vr => (
                <tr key={vr.version_id} className={darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}>
                  <td className="py-1 font-mono">{vr.sheet_number}</td>
                  <td className="py-1">{vr.sheet_title}</td>
                  <td className="py-1">{vr.revision_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function IssueSetDialog({ projectId, drawings, phases, onClose, onIssued }) {
  const [name, setName] = useState('');
  const [phase, setPhase] = useState('CD');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState({});  // drawing_id -> version_id
  const [busy, setBusy] = useState(false);

  const toggle = (dId, vId) => {
    setSelected(prev => {
      const n = { ...prev };
      if (n[dId] === vId) delete n[dId]; else n[dId] = vId;
      return n;
    });
  };

  const submit = async () => {
    if (!name.trim()) return toast.error('Set name is required');
    const versions = Object.entries(selected).map(([drawing_id, version_id]) => ({ drawing_id, version_id }));
    if (versions.length === 0) return toast.error('Pick at least one drawing');
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/plan-room/sets/issue`, {
        name: name.trim(), phase, issue_date: issueDate, note: note.trim() || null, versions,
      });
      onIssued();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue set');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="plan-room-issue-dialog">
        <DialogHeader>
          <DialogTitle>Issue Set</DialogTitle>
          <DialogDescription>
            Group already-uploaded drawings into a named, phase-aware set. A new
            revision of a set supersedes the previous one automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Set name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. A-Set Cabinets"
              data-testid="plan-room-issue-name"
            />
          </div>
          <div>
            <Label>Phase</Label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger data-testid="plan-room-issue-phase"><SelectValue /></SelectTrigger>
              <SelectContent>
                {phases.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Issue date *</Label>
            <Input
              type="date" value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              data-testid="plan-room-issue-date"
            />
          </div>
          <div>
            <Label>Cover note</Label>
            <Input
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional" data-testid="plan-room-issue-note"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mt-3 border rounded p-2">
          <p className="text-xs text-muted-foreground mb-2">
            Pick the drawings + current versions to include ({Object.keys(selected).length} selected):
          </p>
          {drawings.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              Upload drawings first — then come back to issue them as a set.
            </p>
          )}
          {drawings.map(d => {
            const current = (d.versions || []).find(v => v.status === 'current') || (d.versions || [])[0];
            if (!current) return null;
            const isSel = selected[d.id] === current.id;
            return (
              <label
                key={d.id}
                className={`flex items-center gap-3 px-2 py-1.5 rounded cursor-pointer ${isSel ? 'bg-[#70170F]/10' : 'hover:bg-gray-50'}`}
                data-testid={`plan-room-issue-drawing-${d.id}`}
              >
                <input
                  type="checkbox" checked={isSel}
                  onChange={() => toggle(d.id, current.id)}
                />
                <span className="font-mono text-xs w-20">{d.drawing_number}</span>
                <span className="text-sm flex-1 truncate">{d.title}</span>
                <Badge variant="outline" className="text-[10px]">v{current.version_number}</Badge>
              </label>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy}
            className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            data-testid="plan-room-issue-submit-btn"
          >
            {busy ? 'Issuing…' : 'Issue Set'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransmitDialog({ projectId, setId, onClose, onSent }) {
  // Fix Round v3 — contact picker (same pattern as AddTeamMemberDialog).
  //
  // Prior implementation was free-text Name/Email. That broke the
  // portal `recipients ∩ memberships` rule because no contact_id /
  // contractor_id ever got bound, and the contact-timeline write in
  // transmit_set skipped every row. The rebuild:
  //   * Fetches tenant contacts + project members on open.
  //   * Team members render as one-click chips at the top.
  //   * Search + Favorites toggle over remaining contacts.
  //   * Multi-select — selected chips accumulate.
  //   * Inline "New contact" escape hatch that dedupes by email
  //     against the existing /api/contacts endpoint (backend upserts).
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]); // [{contact, party_type}]
  const [search, setSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showInline, setShowInline] = useState(false);
  const [inline, setInline] = useState({ name: '', company: '', email: '', phone: '' });
  const [method, setMethod] = useState('portal');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/contacts?limit=1000`).catch(() => ({ data: { contacts: [] } })),
      axios.get(`${API}/projects/${projectId}/memberships`).catch(() => ({ data: { memberships: [] } })),
    ]).then(([cRes, mRes]) => {
      setContacts(cRes.data.contacts || cRes.data.results || cRes.data || []);
      setMembers(mRes.data.memberships || mRes.data || []);
    }).finally(() => setLoading(false));
  }, [projectId]);

  const isSelected = (mid) => selectedContacts.some((s) => s._membership_id === mid);
  const toggleContact = (contact) => {
    setSelectedContacts((prev) => {
      if (prev.some((s) => s._membership_id === contact._membership_id)) {
        return prev.filter((s) => s._membership_id !== contact._membership_id);
      }
      return [...prev, { ...contact, party_type: contact.party_type || 'contact' }];
    });
  };

  // v7.3 (2026-07-13) — Stage D · "every membership renders as a chip" fix.
  //
  // Contract: `members` is the response from
  //   GET /api/projects/{pid}/memberships?include_removed=false
  // Every element of that array is a project participant the user
  // added intentionally. It MUST render as exactly one chip. Nothing
  // in the frontend is allowed to filter that list further.
  //
  // Prior v7.2 code had three latent defects that could each silently
  // eat a row:
  //   1. `.filter((c) => c.name || c.email)` dropped rows whose
  //      denormalised name/email happened to be empty (corrupt legacy
  //      backfill data, or contacts create endpoint that stores
  //      first_name/last_name instead of `name`).
  //   2. Synthetic id `mem-${m.id || Math.random()}` used Math.random()
  //      as a fallback → non-deterministic React key (chips would
  //      re-mount on every render and flicker off + on).
  //   3. `submit()` derived `party_type` from a hardcoded default in
  //      `toggleContact` — every chip was sent as `contractor`
  //      regardless of the actual membership's party_type. A row for
  //      an internal_user (staff) got routed as a contractor, breaking
  //      the portal `recipients ∩ memberships` intersection.
  //
  // Fix: build chips DIRECTLY from every membership row, using the
  // membership's OWN denormalised fields as the source of truth (name,
  // email, company_name, party_type, party ids). The tenant `contacts`
  // list is used ONLY as a backfill for cosmetic fields that may be
  // richer there (e.g. company). No membership is ever dropped.
  const contactById = new Map(contacts.map((c) => [c.id, c]));
  const contactByContractorId = new Map(
    contacts.filter((c) => c.contractor_id).map((c) => [c.contractor_id, c]),
  );
  const memberContacts = members.map((m) => {
    const linked = (m.contact_id && contactById.get(m.contact_id))
      || (m.contractor_id && contactByContractorId.get(m.contractor_id))
      || null;
    return {
      // React key — guaranteed unique per membership row.
      _membership_id: m.id,
      // party routing — sent VERBATIM to /transmit so the backend
      // recipients ∩ memberships intersection works for every party_type.
      party_type: m.party_type || 'contact',
      party_id: m.contractor_id || m.contact_id || m.user_id || null,
      // ids preserved for the recipient row that transmit_set writes.
      id: m.contact_id || m.contractor_id || m.user_id || m.id,
      contact_id: m.contact_id || null,
      contractor_id: m.contractor_id || null,
      user_id: m.user_id || null,
      // Display fields — prefer the membership row's denormalised
      // values (single source of truth), fall back to the linked
      // contact record when a field is empty.
      name: m.name
        || (linked && (linked.name || linked.full_name
          || [linked.first_name, linked.last_name].filter(Boolean).join(' ').trim()))
        || m.email
        || (linked && linked.email)
        || m.role
        || 'Member',
      email: m.email || (linked && linked.email) || null,
      company_name: m.company_name
        || (linked && (linked.company || linked.company_name))
        || null,
      role: m.role || null,
    };
  });
  // Every chip's `_membership_id` is unique (UUID from Mongo), so the
  // "already a member" set is built off that. Contacts list still
  // shows every non-member contact so the tenant can add ad-hoc
  // recipients from outside the project team.
  const memberContactIdSet = new Set(
    memberContacts.map((c) => c.contact_id).filter(Boolean),
  );
  const otherContacts = contacts.filter((c) => !memberContactIdSet.has(c.id));

  const filteredOthers = otherContacts.filter((c) => {
    if (favoritesOnly && !(c.is_favorite || c.favorite)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        (c.name || c.full_name || '').toLowerCase().includes(q)
        || (c.company || c.company_name || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q)
      );
    }
    return true;
  }).slice(0, 200);

  const submit = async () => {
    if (!selectedContacts.length) return toast.error('Pick at least one recipient');
    // 2026-07-13 — Portal method requires a portal identity per
    // recipient. Block the send with a clear on-screen error instead of
    // firing a 400 that the operator has to decode. Email method still
    // accepts plain contacts (backend accepts it too).
    if (method === 'portal') {
      const badContacts = selectedContacts.filter((c) => (
        (c.party_type || 'contact') === 'contact'
        && !c.contractor_id
        && !c.linked_contractor_id
      ));
      if (badContacts.length) {
        const names = badContacts.slice(0, 3).map((c) => c.name || c.email || 'Unnamed').join(', ');
        return toast.error(
          `Portal delivery needs a portal identity for every recipient. ` +
          `${badContacts.length === 1 ? 'This contact has' : `${badContacts.length} of these contacts have`} ` +
          `no linked contractor: ${names}${badContacts.length > 3 ? '…' : ''}. ` +
          `Switch Method to Email, or link the contact to a contractor first.`,
          { duration: 10000 },
        );
      }
    }
    setBusy(true);
    try {
      // v7.3: `party_type` is now sourced from the membership row (via
      // memberContacts) or defaults to `contact` for ad-hoc contacts
      // added via the search list. `party_id` prefers contractor_id
      // (portal identity) but falls back to contact/user id. This
      // matches the backend `recipients ∩ memberships` intersection
      // in transmit_set for every party_type.
      const recipients = selectedContacts.map((c) => ({
        party_type: c.party_type || 'contact',
        party_id: c.party_id || c.contractor_id || c.contact_id || c.id,
        contact_id: c.contact_id || (c.contractor_id ? null : c.id) || null,
        name: c.name || c.full_name || c.email || 'Contact',
        email: c.email || null,
        company_name: c.company_name || c.company || null,
      }));
      await axios.post(`${API}/projects/${projectId}/plan-room/sets/${setId}/transmit`, {
        recipients, method, note: note.trim() || null,
      });
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send transmittal');
    } finally { setBusy(false); }
  };

  const submitInlineContact = async () => {
    if (!inline.name.trim() && !inline.email.trim()) return toast.error('Name or email required');
    try {
      const r = await axios.post(`${API}/contacts`, {
        name: inline.name.trim() || inline.email.trim(),
        company: inline.company.trim() || null,
        email: inline.email.trim() || null,
        phone: inline.phone.trim() || null,
      });
      const c = r.data.contact || r.data;
      if (!c?.id) throw new Error('Contact create returned no id');
      // Also refresh the tenant list so the new chip appears with any
      // dedupe-upsert side-effects the backend applied.
      setContacts((prev) => {
        const exists = prev.find((x) => x.id === c.id);
        return exists ? prev : [c, ...prev];
      });
      // Ad-hoc contact from the search flow — treat as a plain
      // `contact` recipient. Synthesise a `_membership_id` from the
      // contact.id so isSelected / toggleContact keep working (all
      // three code paths key off `_membership_id`).
      setSelectedContacts((prev) => [...prev, {
        _membership_id: `contact:${c.id}`,
        id: c.id,
        contact_id: c.id,
        contractor_id: null,
        user_id: null,
        party_type: 'contact',
        party_id: c.id,
        name: c.name || c.full_name || c.email,
        email: c.email || null,
        company_name: c.company || c.company_name || null,
      }]);
      setInline({ name: '', company: '', email: '', phone: '' });
      setShowInline(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create contact');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="plan-room-transmit-dialog">
        <DialogHeader>
          <DialogTitle>Transmit Set</DialogTitle>
          <DialogDescription>
            Pick recipients. Project members shown as one-click chips. Search over all tenant contacts. Selecting binds contact_id + contractor_id so the portal Plans tab sees this transmittal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto">
          {/* Selected chips */}
          {selectedContacts.length > 0 && (
            <div className="flex flex-wrap gap-1.5 p-2 rounded border bg-gray-50" data-testid="tx-selected-chips">
              {selectedContacts.map((c) => (
                <Badge
                  key={c._membership_id || c.id}
                  variant="secondary"
                  className="cursor-pointer bg-[#70170F] text-white hover:bg-[#70170F]/80"
                  onClick={() => toggleContact(c)}
                  data-testid={`tx-selected-chip-${c._membership_id || c.id}`}
                >
                  {c.name || c.full_name || c.email}
                  <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
            </div>
          )}

          {/* Project members — one-click chips. v7.3: chip count MUST
              equal memberships.length for the project. If you see a
              missing chip, the row is missing from db.project_memberships
              (not filtered here). */}
          {memberContacts.length > 0 && (
            <div data-testid="tx-project-members">
              <Label className="text-xs">
                Project members ({memberContacts.length})
              </Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {memberContacts.map((c) => (
                  <Button
                    key={c._membership_id}
                    type="button" size="sm"
                    variant={isSelected(c._membership_id) ? 'default' : 'outline'}
                    onClick={() => toggleContact(c)}
                    className={isSelected(c._membership_id) ? 'bg-[#70170F] text-white' : ''}
                    data-testid={`tx-member-chip-${c._membership_id}`}
                    title={c.role ? `${c.name} — ${c.role}` : c.name}
                  >
                    {c.name || c.full_name || c.email}
                    {c.company_name && <span className="opacity-70 ml-1">· {c.company_name}</span>}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Search + filters + inline escape hatch */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name / company / email"
                className="pl-8" data-testid="tx-recipient-search"
              />
            </div>
            <Button type="button" size="sm" variant={favoritesOnly ? 'default' : 'outline'}
              onClick={() => setFavoritesOnly((v) => !v)}
              data-testid="tx-recipient-favorites-toggle"
            >
              <Star className="w-4 h-4 mr-1" /> Favorites
            </Button>
            <Button type="button" size="sm" variant="outline"
              onClick={() => setShowInline(true)}
              data-testid="tx-recipient-new-btn"
            >
              <PlusCircle className="w-4 h-4 mr-1" /> New
            </Button>
          </div>

          {/* Inline create (escape hatch) */}
          {showInline && (
            <div className="p-3 border rounded bg-yellow-50" data-testid="tx-recipient-inline">
              <p className="text-xs text-muted-foreground mb-2">Create a new contact. Deduped by email on the server.</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Name *" value={inline.name} onChange={(e) => setInline((v) => ({ ...v, name: e.target.value }))} data-testid="tx-recipient-inline-name" />
                <Input placeholder="Company" value={inline.company} onChange={(e) => setInline((v) => ({ ...v, company: e.target.value }))} />
                <Input placeholder="Email" value={inline.email} onChange={(e) => setInline((v) => ({ ...v, email: e.target.value }))} data-testid="tx-recipient-inline-email" />
                <Input placeholder="Phone" value={inline.phone} onChange={(e) => setInline((v) => ({ ...v, phone: e.target.value }))} />
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={submitInlineContact} data-testid="tx-recipient-inline-submit">Add + select</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowInline(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Other tenant contacts */}
          <div className="border rounded max-h-64 overflow-y-auto" data-testid="tx-contacts-list">
            {loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading contacts…</div>
            ) : filteredOthers.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {contacts.length === 0 ? 'No contacts yet — click "New" to add one.' : 'No contacts match your filters.'}
              </div>
            ) : filteredOthers.map((c) => {
              // v7.3 — ad-hoc contacts get a synthetic `contact:<id>`
              // `_membership_id` so isSelected/toggleContact key on the
              // same field for every code path.
              const chip = {
                _membership_id: `contact:${c.id}`,
                id: c.id,
                contact_id: c.id,
                contractor_id: null,
                user_id: null,
                party_type: 'contact',
                party_id: c.id,
                name: c.name || c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.email,
                email: c.email || null,
                company_name: c.company || c.company_name || null,
              };
              return (
                <div
                  key={c.id}
                  onClick={() => toggleContact(chip)}
                  className={`p-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${isSelected(chip._membership_id) ? 'bg-[#70170F]/10' : ''}`}
                  data-testid={`tx-contact-row-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{chip.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {(c.company || c.company_name) && <span>{c.company || c.company_name} · </span>}
                      {c.email}
                    </div>
                  </div>
                  {isSelected(chip._membership_id) && <Badge className="bg-[#70170F] text-white text-[10px]">selected</Badge>}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="tx-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portal">Portal</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="hand">Hand-delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Cover note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="tx-note-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit} disabled={busy || !selectedContacts.length}
            className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
            data-testid="plan-room-tx-submit-btn"
          >
            {busy ? 'Sending…' : `Send transmittal (${selectedContacts.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TxDetailDialog({ transmittal: initialTx, onClose, projectId }) {
  const [transmittal, setTransmittal] = useState(initialTx);
  const [resendingIdx, setResendingIdx] = useState(null);

  const doResend = async (idx) => {
    setResendingIdx(idx);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/plan-room/transmittals/${transmittal.id}/resend`,
        { recipient_index: idx },
      );
      setTransmittal(r.data.transmittal);
      if (r.data.sent_ok) {
        toast.success('Email resent');
      } else {
        toast.error(`Resend failed: ${r.data.email_error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingIdx(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="plan-room-tx-detail-dialog">
        <DialogHeader>
          <DialogTitle>{transmittal.set_name}</DialogTitle>
          <DialogDescription>
            Sent {fmt(transmittal.sent_at)} · via {transmittal.method}
            {transmittal.sent_by_name ? ` · by ${transmittal.sent_by_name}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {transmittal.note && (
            <p className="text-sm bg-gray-50 rounded p-2 italic">{transmittal.note}</p>
          )}
          <div>
            <h4 className="text-xs uppercase tracking-wide font-semibold mb-1">Recipients</h4>
            <ul className="space-y-2 text-sm">
              {transmittal.recipients.map((r, i) => {
                const sentIso = r.sent_at;
                const sentOk = r.sent_ok;
                const err = r.email_error;
                const viewedIso = r.first_viewed_at;
                const canResend = !!r.email && (sentOk === false || !!err);
                return (
                  <li
                    key={i}
                    className="rounded border border-gray-200 p-2 space-y-1"
                    data-testid={`tx-recipient-row-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <b>{r.name}</b>
                        {r.email && <span className="text-muted-foreground text-xs"> · {r.email}</span>}
                        {r.company_name && <span className="text-muted-foreground text-xs"> · {r.company_name}</span>}
                      </div>
                      <Badge variant="outline" className="text-[10px] shrink-0">{r.party_type}</Badge>
                    </div>
                    {/* Sent status */}
                    <div
                      className={`text-xs flex items-center gap-1.5 ${sentOk === false || err ? 'text-red-600' : sentOk ? 'text-emerald-600' : 'text-gray-500'}`}
                      data-testid={`tx-recipient-sent-${i}`}
                    >
                      {sentOk === true && <span>✓ Sent {fmt(sentIso)}</span>}
                      {sentOk === false && (
                        <span data-testid={`tx-recipient-sent-error-${i}`}>✗ Send failed{err ? ` — ${err}` : ''}</span>
                      )}
                      {sentOk === undefined && sentOk !== false && !sentIso && (
                        <span>Not sent</span>
                      )}
                    </div>
                    {/* Viewed status */}
                    <div
                      className={`text-xs ${viewedIso ? 'text-emerald-700' : 'text-gray-500'}`}
                      data-testid={`tx-recipient-viewed-${i}`}
                    >
                      {viewedIso ? `👁 Viewed ${fmt(viewedIso)}` : 'Not yet viewed'}
                    </div>
                    {canResend && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resendingIdx === i}
                        onClick={() => doResend(i)}
                        className="h-7 text-xs mt-1"
                        data-testid={`tx-recipient-resend-${i}`}
                      >
                        {resendingIdx === i ? 'Resending…' : 'Resend email'}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
